// apps/web/src/features/clients/clientDomainErrors.ts
//
// Narrows the client aggregate's typed 409 envelopes into dispositions the UI
// can act on. Without this, every conflict collapses into the generic
// `errors:domain.conflict` copy ("refresh the page"), which tells an operator
// nothing about which field or which archived row blocked the write (AC-9).
//
// The wire metas are validated with the SHARED schemas so a backend shape
// change fails here loudly instead of being silently mis-rendered.
import {
  ClientArchivedReadonlyMetaSchema,
  ClientChildArchivedMetaSchema,
  ClientVersionRestoreAmbiguousMetaSchema,
  ErrorCode,
} from '@magimp/shared';
import { ApiClientError, NetworkError, ThrottleError } from '../../api/errors.js';

/** Global unique slots the aggregate reports on a duplicate write. */
export type ClientDuplicateField = 'name' | 'phone' | 'inn';

export type ClientConflict =
  /** A global unique slot is taken; `field` marks the input to correct. */
  | { kind: 'duplicate-field'; field: ClientDuplicateField }
  /** The parent client is archived — restore it before editing children. */
  | { kind: 'parent-archived'; clientId: string }
  /** An archived legal entity / bank set must be restored before editing. */
  | { kind: 'child-archived'; child: 'legal-entity' | 'requisites'; childId: string }
  /** The snapshot cannot prove which bank rows it belonged to; no partial write. */
  | {
      kind: 'restore-ambiguous';
      versionId: string;
      legalEntityIds: readonly string[];
    };

function isDuplicateField(value: unknown): value is ClientDuplicateField {
  return value === 'name' || value === 'phone' || value === 'inn';
}

/**
 * @aiPurpose narrow an unknown mutation error into a typed client-aggregate
 *   conflict. Returns `null` for anything else so the caller keeps its generic
 *   toast path.
 * @aiInvariant `meta` is parsed with the shared schemas, never duck-typed: an
 *   unrecognised 409 must fall through to the generic path rather than be
 *   rendered as a conflict it is not.
 */
export function parseClientConflict(err: unknown): ClientConflict | null {
  if (!(err instanceof ApiClientError)) return null;

  if (err.code === ErrorCode.CLIENT_ARCHIVED_READONLY) {
    const parsed = ClientArchivedReadonlyMetaSchema.safeParse(err.meta);
    if (!parsed.success) return null;
    return { kind: 'parent-archived', clientId: parsed.data.clientId };
  }

  if (err.code !== ErrorCode.DOMAIN_CONFLICT) return null;

  const childArchived = ClientChildArchivedMetaSchema.safeParse(err.meta);
  if (childArchived.success) {
    return {
      kind: 'child-archived',
      child: childArchived.data.kind === 'legal-entity-archived' ? 'legal-entity' : 'requisites',
      childId: childArchived.data.childId,
    };
  }

  const ambiguous = ClientVersionRestoreAmbiguousMetaSchema.safeParse(err.meta);
  if (ambiguous.success) {
    return {
      kind: 'restore-ambiguous',
      versionId: ambiguous.data.versionId,
      legalEntityIds: ambiguous.data.legalEntityIds,
    };
  }

  // Legacy duplicate-slot shape (`{ field }`), preserved verbatim: it predates
  // the kinded metas above and is still what the uniqueness guard emits.
  const meta = err.meta;
  if (meta != null && typeof meta === 'object') {
    const field = (meta as Record<string, unknown>)['field'];
    if (isDuplicateField(field)) return { kind: 'duplicate-field', field };
  }
  return null;
}

/** i18n keys the conflict dispositions map onto, kept as a closed union. */
export type ClientConflictMessageKey =
  | 'clients:conflict.nameTaken'
  | 'clients:conflict.phoneTaken'
  | 'clients:conflict.innTaken'
  | 'clients:conflict.clientArchived'
  | 'clients:conflict.legalArchived'
  | 'clients:conflict.requisitesArchived'
  | 'clients:conflict.restoreAmbiguous';

/** Actionable copy for one conflict: what is wrong and what to do next. */
export function clientConflictMessageKey(conflict: ClientConflict): ClientConflictMessageKey {
  switch (conflict.kind) {
    case 'duplicate-field':
      if (conflict.field === 'name') return 'clients:conflict.nameTaken';
      return conflict.field === 'phone'
        ? 'clients:conflict.phoneTaken'
        : 'clients:conflict.innTaken';
    case 'parent-archived':
      return 'clients:conflict.clientArchived';
    case 'child-archived':
      return conflict.child === 'legal-entity'
        ? 'clients:conflict.legalArchived'
        : 'clients:conflict.requisitesArchived';
    case 'restore-ambiguous':
      return 'clients:conflict.restoreAmbiguous';
  }
}

/**
 * True when the request produced no usable server verdict, so the write MAY
 * have been applied. Callers must keep the draft and let the operator inspect
 * the refreshed data — replaying a POST blind could create a second row.
 *
 * The classification is deliberately inverted: a CONFIRMED refusal is the
 * narrow case (the server answered with a 4xx domain verdict, or refused the
 * request outright), and everything else is unknown. A 502 from a proxy, a
 * truncated success body (`SyntaxError`) and a 201 that fails schema parsing
 * (`ZodError`) all mean the row may exist.
 *
 * INVARIANT: `ThrottleError` extends `NetworkError` but IS a verdict — the
 * request was refused before reaching the handler — so it stays retryable.
 */
export function isUncertainOutcome(err: unknown): boolean {
  if (err instanceof ThrottleError) return false;
  if (err instanceof NetworkError) return true;
  if (err instanceof ApiClientError) return err.httpStatus >= 500;
  return true;
}
